(() => {
  const MOBILE_QUERY = '(max-width: 767px)';

  const initProductSlider = (slider) => {
    if (!slider || slider.dataset.routineSliderInitialised === 'true') return;
    slider.dataset.routineSliderInitialised = 'true';

    const area = slider.closest('.prohealth-routine__products-area');
    const prev = area?.querySelector('[data-routine-slider-prev]');
    const next = area?.querySelector('[data-routine-slider-next]');
    if (!prev || !next) return;

    const getProduct = () => slider.querySelector('.prohealth-routine__product');
    const getScrollAmount = () => {
      const product = getProduct();
      if (!product) return slider.clientWidth * 0.8;
      const styles = window.getComputedStyle(slider);
      const gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;
      return product.getBoundingClientRect().width + gap;
    };

    const updateButtons = () => {
      const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
      const tolerance = 3;
      prev.disabled = maxScroll <= tolerance || slider.scrollLeft <= tolerance;
      next.disabled = maxScroll <= tolerance || slider.scrollLeft >= maxScroll - tolerance;
    };

    prev.addEventListener('click', () => slider.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' }));
    next.addEventListener('click', () => slider.scrollBy({ left: getScrollAmount(), behavior: 'smooth' }));
    slider.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons, { passive: true });
    updateButtons();
  };

  const initCopyToggle = (button) => {
    if (!button || button.dataset.routineCopyInitialised === 'true') return;
    button.dataset.routineCopyInitialised = 'true';

    const section = button.closest('[data-prohealth-routine]');
    const targetId = button.getAttribute('aria-controls');
    const copy = targetId ? section?.querySelector(`#${CSS.escape(targetId)}`) : null;
    if (!copy) return;

    const setState = (open) => {
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      button.setAttribute('aria-label', open ? (button.dataset.closeLabel || 'Hide details') : (button.dataset.openLabel || 'Show more details'));
      copy.classList.toggle('is-open', open);
    };

    button.addEventListener('click', () => setState(button.getAttribute('aria-expanded') !== 'true'));
    setState(false);
  };

  const getCurrentLayout = (section) => {
    const isMobile = window.matchMedia(MOBILE_QUERY).matches;
    return isMobile ? (section.dataset.mobileLayout || 'flow') : (section.dataset.desktopLayout || 'flow');
  };

  const initTabs = (section) => {
    const tabs = Array.from(section.querySelectorAll('[data-routine-tab]'));
    const steps = Array.from(section.querySelectorAll('[data-routine-step]'));
    if (!tabs.length || !steps.length) return;

    let activeTarget = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.routineTarget || tabs[0]?.dataset.routineTarget;

    const activate = (targetId, focusTab = false) => {
      if (!targetId) return;
      activeTarget = targetId;

      tabs.forEach((tab) => {
        const active = tab.dataset.routineTarget === targetId;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active && focusTab) tab.focus();
      });

      if (getCurrentLayout(section) !== 'tabs') return;

      steps.forEach((step) => {
        const active = step.id === targetId;
        step.hidden = !active;
        step.setAttribute('role', 'tabpanel');
        step.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (active) {
          step.querySelectorAll('[data-routine-slider]').forEach((slider) => {
            initProductSlider(slider);
            requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
          });
        }
      });
    };

    const applyLayout = () => {
      const tabsMode = getCurrentLayout(section) === 'tabs';
      if (tabsMode) {
        activate(activeTarget || tabs[0]?.dataset.routineTarget);
      } else {
        steps.forEach((step) => {
          step.hidden = false;
          step.removeAttribute('role');
          step.removeAttribute('aria-hidden');
        });
      }
      section.dataset.activeRoutineLayout = tabsMode ? 'tabs' : 'flow';
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab.dataset.routineTarget));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        activate(tabs[nextIndex].dataset.routineTarget, true);
      });
    });

    const media = window.matchMedia(MOBILE_QUERY);
    if (media.addEventListener) media.addEventListener('change', applyLayout);
    else media.addListener(applyLayout);

    section._routineActivateTab = activate;
    section._routineApplyLayout = applyLayout;
    applyLayout();
  };

  const initFlowNavigation = (section) => {
    const links = Array.from(section.querySelectorAll('[data-routine-nav-link]'));
    const steps = Array.from(section.querySelectorAll('[data-routine-step]'));
    if (!links.length || !steps.length) return;

    const setActive = (id) => {
      links.forEach((link) => {
        const active = link.getAttribute('href') === `#${id}`;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'step');
        else link.removeAttribute('aria-current');
      });
    };

    links.forEach((link) => {
      link.addEventListener('click', (event) => {
        if (getCurrentLayout(section) !== 'flow') return;
        const targetId = link.getAttribute('href')?.slice(1);
        const target = targetId ? section.querySelector(`#${CSS.escape(targetId)}`) : null;
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActive(targetId);
      });
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (getCurrentLayout(section) !== 'flow') return;
        const visible = entries
          .filter((entry) => entry.isIntersecting && !entry.target.hidden)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) setActive(visible.target.id);
      }, { rootMargin: '-20% 0px -55% 0px', threshold: [0.05, 0.2, 0.5] });
      steps.forEach((step) => observer.observe(step));
    }
  };

  const initRoutineSection = (section) => {
    if (!section) return;

    section.querySelectorAll('[data-routine-slider]').forEach(initProductSlider);
    section.querySelectorAll('[data-routine-copy-toggle]').forEach(initCopyToggle);

    if (section.dataset.routineInitialised === 'true') {
      section._routineApplyLayout?.();
      return;
    }
    section.dataset.routineInitialised = 'true';

    initTabs(section);
    initFlowNavigation(section);
  };

  const initAll = (root = document) => {
    root.querySelectorAll('[data-prohealth-routine]').forEach(initRoutineSection);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target.closest?.('[data-routine-step]');
    const section = block?.closest?.('[data-prohealth-routine]');
    if (!block || !section || getCurrentLayout(section) !== 'tabs') return;
    section._routineActivateTab?.(block.id);
  });
})();
